import { Button, Card, Checkbox, Dialog, Empty, Input, NavBar, Space, Tabs, Tag, Toast } from "antd-mobile";
import { ArrowDown, ArrowUp, ImagePlus, Plus, RefreshCw, Save, Trash2 } from "lucide-react";
import type { ReactNode } from "react";
import { useEffect, useMemo, useState } from "react";
import { changeAdminPassword, fetchAdminJson, loginAdmin, postAdminJson } from "../api";
import { buildHealthCertificateSummary, calculateHealthStatus, HEALTH_STATUS_LABELS, maskIdNumber } from "../shared/health";
import type {
  AttendanceRule,
  HealthCertificateRow,
  HealthStatus,
  LicenseCard,
  OnvifDiscoveredDevice,
  PublicDisplaySnapshot,
  QualityReportUpload,
  ReportCategory,
  SampleRetentionRow
} from "../shared/types";

interface AdminScreenProps {
  initialSnapshot: PublicDisplaySnapshot;
}

interface AdminCameraRow {
  id: string;
  name: string;
  slot: 1 | 2 | 3 | 4;
  onvifUrl: string;
  ipAddress: string;
  rtspUrl: string;
  username: string;
  password: string;
  hasPassword: boolean;
  clearPassword: boolean;
}

interface AdminCameraResponse {
  cameras?: Array<Partial<AdminCameraRow> & { slot: 1 | 2 | 3 | 4 }>;
  discoveredDevices?: OnvifDiscoveredDevice[];
}

interface AssetUploadResponse {
  assetUrl: string;
  contentType: string;
  key: string;
  ok: boolean;
}

interface SnapshotSaveResponse {
  ok?: boolean;
  snapshot?: PublicDisplaySnapshot;
}

const REPORT_CATEGORIES: Array<{ key: ReportCategory; label: string }> = [
  { key: "meat", label: "肉类" },
  { key: "produce", label: "蔬菜水果" },
  { key: "dryGoods", label: "干杂调料" },
  { key: "staple", label: "米面油" }
];

export function AdminScreen({ initialSnapshot }: AdminScreenProps) {
  const [sessionToken, setSessionToken] = useState(() => window.localStorage.getItem("food-safety-display:admin-session") ?? "");
  const [loginPassword, setLoginPassword] = useState("");
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [snapshot, setSnapshot] = useState<PublicDisplaySnapshot>(() => normalizeSnapshot(initialSnapshot));
  const [message, setMessage] = useState("待保存");
  const [cameraRows, setCameraRows] = useState<AdminCameraRow[]>(() => createDefaultCameraRows(initialSnapshot));
  const [discoveredDevices, setDiscoveredDevices] = useState<OnvifDiscoveredDevice[]>([]);
  const [cameraMessage, setCameraMessage] = useState("待加载");
  const [isLoadingSnapshot, setIsLoadingSnapshot] = useState(false);
  const [isLoadingCameras, setIsLoadingCameras] = useState(false);
  const [isLoggingIn, setIsLoggingIn] = useState(false);
  const [isChangingPassword, setIsChangingPassword] = useState(false);
  const [isSavingCameras, setIsSavingCameras] = useState(false);
  const [isSavingSnapshot, setIsSavingSnapshot] = useState(false);
  const cameraSummary = useMemo(() => cameraRows.map((camera) => `${camera.slot}. ${camera.name || "未命名"}`).join(" / "), [cameraRows]);
  const healthSummary = useMemo(() => buildHealthCertificateSummary(snapshot.healthCertificates.rows), [snapshot.healthCertificates.rows]);
  const isAuthenticated = sessionToken.trim().length > 0;

  useEffect(() => {
    document.body.classList.add("admin-route");
    document.documentElement.classList.add("admin-route");
    return () => {
      document.body.classList.remove("admin-route");
      document.documentElement.classList.remove("admin-route");
    };
  }, []);

  useEffect(() => {
    if (sessionToken.trim()) {
      void loadSnapshot(sessionToken);
      void loadCameras(sessionToken);
    }
  }, []);

  async function handleLogin() {
    setIsLoggingIn(true);
    try {
      const response = await loginAdmin(loginPassword);
      window.localStorage.setItem("food-safety-display:admin-session", response.sessionToken);
      window.localStorage.removeItem("food-safety-display:admin-token");
      setSessionToken(response.sessionToken);
      setLoginPassword("");
      await Promise.all([loadSnapshot(response.sessionToken), loadCameras(response.sessionToken)]);
      Toast.show({ content: "登录成功" });
    } catch (error) {
      await Dialog.alert({
        content: getErrorMessage(error),
        title: "登录失败"
      });
    } finally {
      setIsLoggingIn(false);
    }
  }

  function logout() {
    window.localStorage.removeItem("food-safety-display:admin-session");
    setSessionToken("");
    setCurrentPassword("");
    setNewPassword("");
    setConfirmPassword("");
  }

  async function updateLoginPassword() {
    if (newPassword !== confirmPassword) {
      await Dialog.alert({ content: "两次输入的新密码不一致", title: "修改失败" });
      return;
    }
    if (newPassword.trim().length < 6) {
      await Dialog.alert({ content: "新密码至少需要 6 位", title: "修改失败" });
      return;
    }

    setIsChangingPassword(true);
    try {
      await changeAdminPassword(sessionToken, currentPassword, newPassword);
      setCurrentPassword("");
      setNewPassword("");
      setConfirmPassword("");
      Toast.show({ content: "登录密码已修改" });
    } catch (error) {
      await Dialog.alert({
        content: getErrorMessage(error),
        title: "修改失败"
      });
    } finally {
      setIsChangingPassword(false);
    }
  }

  async function loadSnapshot(authToken = sessionToken) {
    setIsLoadingSnapshot(true);
    setMessage("加载中");
    try {
      const response = await fetchAdminJson<PublicDisplaySnapshot | SnapshotSaveResponse>("/api/admin/snapshot", authToken);
      const nextSnapshot = normalizeSnapshot(extractSnapshot(response, snapshot));
      applySnapshot(nextSnapshot);
      setMessage("已加载");
    } catch (error) {
      setMessage("加载失败");
      await Dialog.alert({
        content: getErrorMessage(error),
        title: "加载失败"
      });
    } finally {
      setIsLoadingSnapshot(false);
    }
  }

  async function saveBusinessSnapshot(nextSnapshot: PublicDisplaySnapshot, successMessage: string) {
    const stampedSnapshot = normalizeSnapshot({ ...nextSnapshot, generatedAt: new Date().toISOString() });
    setIsSavingSnapshot(true);
    setMessage("保存中");
    try {
      const response = await postAdminJson<SnapshotSaveResponse>("/api/admin/snapshot", sessionToken, stampedSnapshot);
      applySnapshot(normalizeSnapshot(response.snapshot ?? stampedSnapshot));
      setMessage("已保存");
      Toast.show({ content: successMessage });
    } catch (error) {
      setMessage("保存失败");
      await Dialog.alert({
        content: getErrorMessage(error),
        title: "保存失败"
      });
    } finally {
      setIsSavingSnapshot(false);
    }
  }

  async function loadCameras(authToken = sessionToken) {
    setIsLoadingCameras(true);
    setCameraMessage("加载中");
    try {
      const response = await fetchAdminJson<AdminCameraResponse>("/api/admin/cameras", authToken);
      setCameraRows(mergeCameraRows(response.cameras ?? [], snapshot));
      setDiscoveredDevices(response.discoveredDevices ?? []);
      setCameraMessage("已加载");
    } catch (error) {
      setCameraMessage("加载失败");
      await Dialog.alert({
        content: getErrorMessage(error),
        title: "加载失败"
      });
    } finally {
      setIsLoadingCameras(false);
    }
  }

  async function saveCameras() {
    setIsSavingCameras(true);
    setCameraMessage("保存中");
    try {
      await postAdminJson("/api/admin/cameras", sessionToken, {
        cameras: cameraRows.map(({ hasPassword, ...camera }) => ({
          ...camera,
          password: camera.password,
          clearPassword: camera.clearPassword
        }))
      });
      setCameraRows((rows) =>
        rows.map((row) => ({
          ...row,
          hasPassword: row.clearPassword ? false : row.hasPassword || Boolean(row.password),
          password: "",
          clearPassword: false
        }))
      );
      setCameraMessage("已保存");
      Toast.show({ content: "摄像头配置已保存" });
    } catch (error) {
      setCameraMessage("保存失败");
      await Dialog.alert({
        content: getErrorMessage(error),
        title: "保存失败"
      });
    } finally {
      setIsSavingCameras(false);
    }
  }

  function applySnapshot(nextSnapshot: PublicDisplaySnapshot) {
    setSnapshot(nextSnapshot);
  }

  function updateLicense(index: number, patch: Partial<LicenseCard>) {
    setSnapshot((current) => ({
      ...current,
      licenseCards: ensureLicenseCards(current.licenseCards).map((card, cardIndex) => (cardIndex === index ? { ...card, ...patch } : card))
    }));
  }

  function updateHealthRow(index: number, patch: Partial<HealthCertificateRow>) {
    setSnapshot((current) => ({
      ...current,
      healthCertificates: {
        ...current.healthCertificates,
        rows: current.healthCertificates.rows.map((row, rowIndex) => (rowIndex === index ? { ...row, ...patch } : row))
      }
    }));
  }

  function addHealthRow() {
    setSnapshot((current) => ({
      ...current,
      healthCertificates: {
        ...current.healthCertificates,
        rows: [
          ...current.healthCertificates.rows,
          {
            id: `health-${Date.now()}`,
            name: "",
            idNumber: "",
            idNumberMasked: "",
            hasHealthCertificate: true,
            status: "normal",
            validUntil: "",
            avatarUrl: ""
          }
        ]
      }
    }));
  }

  function removeHealthRow(index: number) {
    setSnapshot((current) => ({
      ...current,
      healthCertificates: {
        ...current.healthCertificates,
        rows: current.healthCertificates.rows.filter((_, rowIndex) => rowIndex !== index)
      }
    }));
  }

  function updateAttendance(patch: Partial<AttendanceRule>) {
    setSnapshot((current) => ({
      ...current,
      attendanceRule: normalizeAttendanceRule({ ...current.attendanceRule, ...patch })
    }));
  }

  function saveAttendanceRule() {
    return saveBusinessSnapshot(
      {
        ...snapshot,
        attendanceRule: normalizeAttendanceRule({
          ...snapshot.attendanceRule,
          resetAt: new Date().toISOString()
        })
      },
      "到店人数规则已保存"
    );
  }

  function updateRetentionRow(index: number, patch: Partial<SampleRetentionRow>) {
    setSnapshot((current) => ({
      ...current,
      sampleRetentionRows: current.sampleRetentionRows.map((row, rowIndex) => (rowIndex === index ? { ...row, ...patch } : row))
    }));
  }

  function addRetentionRow() {
    setSnapshot((current) => ({
      ...current,
      sampleRetentionRows: [
        ...current.sampleRetentionRows,
        {
          id: current.sampleRetentionRows.length + 1,
          dish: "",
          weight: "150g",
          meal: "午餐",
          keeper: ""
        }
      ]
    }));
  }

  function removeRetentionRow(index: number) {
    setSnapshot((current) => ({
      ...current,
      sampleRetentionRows: current.sampleRetentionRows.filter((_, rowIndex) => rowIndex !== index).map((row, rowIndex) => ({ ...row, id: rowIndex + 1 }))
    }));
  }

  function moveRetentionRow(index: number, direction: -1 | 1) {
    setSnapshot((current) => {
      const nextRows = [...current.sampleRetentionRows];
      const targetIndex = index + direction;
      if (targetIndex < 0 || targetIndex >= nextRows.length) {
        return current;
      }
      [nextRows[index], nextRows[targetIndex]] = [nextRows[targetIndex], nextRows[index]];
      return {
        ...current,
        sampleRetentionRows: nextRows.map((row, rowIndex) => ({ ...row, id: rowIndex + 1 }))
      };
    });
  }

  function updateReportUpload(category: ReportCategory, patch: Partial<QualityReportUpload>) {
    setSnapshot((current) => {
      const existing = latestReportByCategory(current.reportUploads, category);
      const nextUpload: QualityReportUpload = {
        category,
        title: REPORT_CATEGORIES.find((item) => item.key === category)?.label ?? category,
        assetUrl: "",
        uploadedAt: "",
        ...existing,
        ...patch
      };
      return {
        ...current,
        reportUploads: [...current.reportUploads.filter((upload) => upload.category !== category), nextUpload]
      };
    });
  }

  function updateCamera(slot: 1 | 2 | 3 | 4, patch: Partial<AdminCameraRow>) {
    setCameraRows((rows) => rows.map((row) => (row.slot === slot ? { ...row, ...patch } : row)));
  }

  function fillDiscoveredDevice(device: OnvifDiscoveredDevice) {
    const emptyRow = cameraRows.find((row) => !row.onvifUrl && !row.rtspUrl);
    if (!emptyRow) {
      Toast.show({ content: "没有空摄像头槽位" });
      return;
    }
    updateCamera(emptyRow.slot, {
      id: device.id || emptyRow.id,
      name: device.name || emptyRow.name,
      onvifUrl: device.xaddr,
      ipAddress: device.ipAddress
    });
    Toast.show({ content: `已填入${emptyRow.slot}号槽位` });
  }

  async function uploadAsset(file: File): Promise<string> {
    const dataBase64 = await readFileAsBase64(file);
    const response = await postAdminJson<AssetUploadResponse>("/api/admin/assets", sessionToken, {
      fileName: file.name,
      contentType: file.type || "application/octet-stream",
      dataBase64
    });
    return response.assetUrl;
  }

  async function handleLicenseUpload(index: number, file: File | undefined) {
    if (!file) {
      return;
    }
    try {
      const assetUrl = await uploadAsset(file);
      updateLicense(index, { imageUrl: assetUrl, updatedAt: new Date().toISOString() });
      Toast.show({ content: "证照图片已上传，记得保存证照" });
    } catch (error) {
      await Dialog.alert({ content: getErrorMessage(error), title: "上传失败" });
    }
  }

  async function handleHealthAvatarUpload(index: number, file: File | undefined) {
    if (!file) {
      return;
    }
    try {
      const avatarUrl = await uploadAsset(file);
      updateHealthRow(index, { avatarUrl });
      Toast.show({ content: "头像已上传，记得保存健康证" });
    } catch (error) {
      await Dialog.alert({ content: getErrorMessage(error), title: "上传失败" });
    }
  }

  async function handleReportUpload(category: ReportCategory, file: File | undefined) {
    if (!file) {
      return;
    }
    try {
      const uploadedAt = new Date().toISOString();
      const assetUrl = await uploadAsset(file);
      const upload: QualityReportUpload = {
        assetUrl,
        category,
        title: REPORT_CATEGORIES.find((item) => item.key === category)?.label ?? category,
        uploadedAt
      };
      await postAdminJson("/api/admin/reports", sessionToken, upload);
      updateReportUpload(category, upload);
      Toast.show({ content: "质检报告已上传并保存" });
    } catch (error) {
      await Dialog.alert({ content: getErrorMessage(error), title: "上传失败" });
    }
  }

  if (!isAuthenticated) {
    return (
      <main className="admin-page admin-login-page">
        <section className="admin-login-panel">
          <Card className="admin-login-card">
            <p>智慧食堂后台</p>
            <h1>智慧食堂·食安公示屏</h1>
            <h2>后台登录</h2>
            <label className="admin-mobile-field">
              <span>登录密码</span>
              <Input
                aria-label="登录密码"
                clearable
                placeholder="请输入登录密码"
                type="password"
                value={loginPassword}
                onChange={setLoginPassword}
                onEnterPress={() => void handleLogin()}
              />
            </label>
            <Button block color="primary" loading={isLoggingIn} onClick={handleLogin}>
              登录
            </Button>
          </Card>
        </section>
      </main>
    );
  }

  return (
    <main className="admin-page">
      <NavBar
        back={null}
        className="admin-mobile-nav"
        right={
          <Space align="center">
            <Tag color="success">已登录</Tag>
            <Button fill="none" size="mini" onClick={logout}>
              退出
            </Button>
          </Space>
        }
      >
        后台维护
      </NavBar>

      <section className="admin-mobile-hero">
        <p>智慧食堂后台</p>
        <h1>智慧食堂·食安公示屏</h1>
        <div className="admin-hero-actions">
          <Button block fill="outline" loading={isLoadingSnapshot} onClick={() => loadSnapshot()}>
            <RefreshCw size={16} />
            刷新公示数据
          </Button>
        </div>
      </section>

      <Tabs className="admin-mobile-tabs" defaultActiveKey="licenses">
        <Tabs.Tab title="证照" key="licenses">
          <section className="admin-mobile-panel">
            <PanelStatus title="营业资质" status={message} description="维护营业执照和食品经营许可证图片，前台按两张证照卡片展示。" />
            <div className="admin-form-card-list">
              {ensureLicenseCards(snapshot.licenseCards).map((card, index) => (
                <Card className="admin-form-card" key={card.id} title={card.title || `${index + 1}号证照`}>
                  <AssetPreview alt={card.title} src={card.imageUrl} />
                  <MobileField label={`${card.title || `${index + 1}号证照`}标题`}>
                    <Input aria-label={`${card.title || `${index + 1}号证照`}标题`} clearable value={card.title} onChange={(value) => updateLicense(index, { title: value })} />
                  </MobileField>
                  <MobileField label="副标题">
                    <Input clearable value={card.subtitle} onChange={(value) => updateLicense(index, { subtitle: value })} />
                  </MobileField>
                  <MobileField label="更新时间">
                    <Input clearable value={card.updatedAt} onChange={(value) => updateLicense(index, { updatedAt: value })} />
                  </MobileField>
                  <FilePicker onChange={(file) => void handleLicenseUpload(index, file)} />
                </Card>
              ))}
            </div>
            <Button block color="primary" loading={isSavingSnapshot} onClick={() => saveBusinessSnapshot({ ...snapshot, licenseCards: ensureLicenseCards(snapshot.licenseCards) }, "证照已保存")}>
              <Save size={16} />
              保存证照
            </Button>
          </section>
        </Tabs.Tab>

        <Tabs.Tab title="健康证" key="health">
          <section className="admin-mobile-panel">
            <Card className="admin-status-card">
              <div className="admin-status-row">
                <span>健康证统计</span>
                <Tag color="primary">自动计算</Tag>
              </div>
              <div className="admin-health-summary">
                <strong>{healthSummary.total}</strong>
                {HEALTH_STATUS_LABELS.map((status) => (
                  <span key={status.value}>
                    {status.label} {healthSummary.counts[status.value]}
                  </span>
                ))}
              </div>
            </Card>
            <div className="admin-form-card-list">
              {snapshot.healthCertificates.rows.map((row, index) => {
                const personLabel = row.name || `${index + 1}号人员`;
                const computedStatus = calculateHealthStatus(row);

                return (
                  <Card
                    className="admin-form-card"
                    key={row.id}
                    title={
                      <div className="admin-card-title-row">
                        <span>{personLabel}</span>
                        <div>
                          <Tag color={healthStatusTagColor(computedStatus)}>{healthStatusLabel(computedStatus)}</Tag>
                          <Button color="danger" fill="none" size="mini" onClick={() => removeHealthRow(index)}>
                            <Trash2 size={16} />
                          </Button>
                        </div>
                      </div>
                    }
                  >
                    <AssetPreview alt={row.name} src={row.avatarUrl} />
                    <MobileField label="姓名">
                      <Input aria-label={`${personLabel}姓名`} clearable value={row.name} onChange={(value) => updateHealthRow(index, { name: value })} />
                    </MobileField>
                    <MobileField label="身份证号码" hint="后台保存完整号码，前台自动掩码显示">
                      <Input
                        aria-label={`${personLabel}身份证号码`}
                        clearable
                        placeholder="请输入完整身份证号码"
                        value={row.idNumber ?? ""}
                        onChange={(value) => updateHealthRow(index, { idNumber: value, idNumberMasked: maskIdNumber(value) })}
                      />
                    </MobileField>
                    <MobileField label="是否有健康证">
                      <select
                        aria-label={`${personLabel}是否有健康证`}
                        className="admin-native-select"
                        value={row.hasHealthCertificate === false ? "no" : "yes"}
                        onChange={(event) => updateHealthRow(index, { hasHealthCertificate: event.target.value === "yes" })}
                      >
                        <option value="yes">有健康证</option>
                        <option value="no">无健康证</option>
                      </select>
                    </MobileField>
                    <MobileField label="有效期">
                      <input
                        aria-label={`${personLabel}健康证有效期`}
                        className="admin-native-date"
                        disabled={row.hasHealthCertificate === false}
                        type="date"
                        value={toDateInputValue(row.validUntil)}
                        onChange={(event) => updateHealthRow(index, { validUntil: event.target.value })}
                      />
                    </MobileField>
                    <FilePicker onChange={(file) => void handleHealthAvatarUpload(index, file)} />
                  </Card>
                );
              })}
            </div>
            <div className="admin-sticky-actions">
              <Button block fill="outline" onClick={addHealthRow}>
                <Plus size={16} />
                新增健康证
              </Button>
              <Button
                block
                color="primary"
                loading={isSavingSnapshot}
                onClick={() =>
                  saveBusinessSnapshot(
                    {
                      ...snapshot,
                      healthCertificates: buildHealthCertificateSummary(snapshot.healthCertificates.rows)
                    },
                    "健康证已保存"
                  )
                }
              >
                <Save size={16} />
                保存健康证
              </Button>
            </div>
          </section>
        </Tabs.Tab>

        <Tabs.Tab title="到店人数" key="attendance">
          <section className="admin-mobile-panel">
            <PanelStatus title="实时到店人数规则" status={message} description="保存后前台下一次刷新会回到起始人数，再按增加频率逐步增加。" />
            <Card className="admin-form-card">
              <MobileField label="开始时间">
                <Input aria-label="开始时间" clearable value={snapshot.attendanceRule.startTime} onChange={(value) => updateAttendance({ startTime: value })} />
              </MobileField>
              <MobileField label="结束时间">
                <Input aria-label="结束时间" clearable value={snapshot.attendanceRule.endTime} onChange={(value) => updateAttendance({ endTime: value })} />
              </MobileField>
              <MobileField label="起始人数">
                <IntegerInput ariaLabel="起始人数" value={snapshot.attendanceRule.startCount} onChange={(value) => updateAttendance({ startCount: value })} />
              </MobileField>
              <MobileField label="目标人数">
                <IntegerInput ariaLabel="目标人数" value={snapshot.attendanceRule.targetCount} onChange={(value) => updateAttendance({ targetCount: value })} />
              </MobileField>
              <RangeField
                description="增加频率表示前台人数每隔多少分钟变化一次。例如设置为 10，保存后先显示起始人数，10 分钟后才增加一次。"
                label="增加频率"
                max={20}
                min={1}
                unit="分钟"
                value={snapshot.attendanceRule.frequencyMinutes ?? 5}
                onChange={(value) => updateAttendance({ frequencyMinutes: value })}
              />
              <RangeField
                description="波动值表示前台人数在阶梯增长基础上的随机浮动范围。例如设置为 8，显示人数会在计算值上下最多浮动 8 人，让变化看起来更自然。"
                label="波动值"
                max={50}
                min={0}
                unit="人"
                value={snapshot.attendanceRule.jitter}
                onChange={(value) => updateAttendance({ jitter: value })}
              />
            </Card>
            <Button block color="primary" loading={isSavingSnapshot} onClick={saveAttendanceRule}>
              <Save size={16} />
              保存到店人数
            </Button>
          </section>
        </Tabs.Tab>

        <Tabs.Tab title="留样" key="samples">
          <section className="admin-mobile-panel">
            <PanelStatus title="留样公示" status={message} description="前台只展示序号、菜品、克重、餐次、留样人五列。保存时会自动重排序号。" />
            <div className="admin-form-card-list">
              {snapshot.sampleRetentionRows.map((row, index) => (
                <Card
                  className="admin-form-card"
                  key={`${row.id}-${index}`}
                  title={
                    <div className="admin-card-title-row">
                      <span>{row.id}号留样</span>
                      <div>
                        <Button disabled={index === 0} fill="none" size="mini" onClick={() => moveRetentionRow(index, -1)}>
                          <ArrowUp size={16} />
                        </Button>
                        <Button disabled={index === snapshot.sampleRetentionRows.length - 1} fill="none" size="mini" onClick={() => moveRetentionRow(index, 1)}>
                          <ArrowDown size={16} />
                        </Button>
                        <Button color="danger" fill="none" size="mini" onClick={() => removeRetentionRow(index)}>
                          <Trash2 size={16} />
                        </Button>
                      </div>
                    </div>
                  }
                >
                  <MobileField label="菜品">
                    <Input aria-label={`${row.id}号菜品`} clearable value={row.dish} onChange={(value) => updateRetentionRow(index, { dish: value })} />
                  </MobileField>
                  <MobileField label="克重">
                    <Input aria-label={`${row.id}号克重`} clearable value={row.weight} onChange={(value) => updateRetentionRow(index, { weight: value })} />
                  </MobileField>
                  <MobileField label="餐次">
                    <Input aria-label={`${row.id}号餐次`} clearable value={row.meal} onChange={(value) => updateRetentionRow(index, { meal: value })} />
                  </MobileField>
                  <MobileField label="留样人">
                    <Input aria-label={`${row.id}号留样人`} clearable value={row.keeper} onChange={(value) => updateRetentionRow(index, { keeper: value })} />
                  </MobileField>
                </Card>
              ))}
            </div>
            <div className="admin-sticky-actions">
              <Button block fill="outline" onClick={addRetentionRow}>
                <Plus size={16} />
                新增留样
              </Button>
              <Button
                block
                color="primary"
                loading={isSavingSnapshot}
                onClick={() =>
                  saveBusinessSnapshot(
                    {
                      ...snapshot,
                      sampleRetentionRows: snapshot.sampleRetentionRows.map((row, index) => ({ ...row, id: index + 1 }))
                    },
                    "留样公示已保存"
                  )
                }
              >
                <Save size={16} />
                保存留样
              </Button>
            </div>
          </section>
        </Tabs.Tab>

        <Tabs.Tab title="质检" key="reports">
          <section className="admin-mobile-panel">
            <PanelStatus title="质检报告更新" status={message} description="四类固定展示。当天上传或手动设置为今天的报告，会在前台显示绿色已更新。" />
            <div className="admin-form-card-list">
              {REPORT_CATEGORIES.map((category) => {
                const upload = latestReportByCategory(snapshot.reportUploads, category.key);
                const isToday = Boolean(upload?.uploadedAt && toLocalDateKey(upload.uploadedAt) === toLocalDateKey(new Date().toISOString()));
                return (
                  <Card
                    className="admin-form-card"
                    key={category.key}
                    title={
                      <div className="admin-card-title-row">
                        <span>{category.label}</span>
                        <Tag color={isToday ? "success" : "danger"}>{isToday ? "已更新" : "未更新"}</Tag>
                      </div>
                    }
                  >
                    <AssetPreview alt={category.label} src={upload?.assetUrl} />
                    <Space block direction="vertical">
                      <FilePicker onChange={(file) => void handleReportUpload(category.key, file)} />
                      <Button block fill="outline" onClick={() => updateReportUpload(category.key, { uploadedAt: new Date().toISOString() })}>
                        标记今日已更新
                      </Button>
                    </Space>
                  </Card>
                );
              })}
            </div>
            <Button block color="primary" loading={isSavingSnapshot} onClick={() => saveBusinessSnapshot(snapshot, "质检报告已保存")}>
              <Save size={16} />
              保存质检
            </Button>
          </section>
        </Tabs.Tab>

        <Tabs.Tab title="摄像头" key="cameras">
          <section className="admin-mobile-panel">
            <Card className="admin-status-card">
              <div className="admin-status-row">
                <span>配置状态</span>
                <Tag color={cameraMessage === "已保存" || cameraMessage === "已加载" ? "success" : "primary"}>{cameraMessage}</Tag>
              </div>
              <p>{cameraSummary}</p>
              <Space block direction="vertical">
                <Button block fill="outline" loading={isLoadingCameras} onClick={() => loadCameras()}>
                  <RefreshCw size={16} />
                  加载摄像头配置
                </Button>
                <Button block color="primary" loading={isSavingCameras} onClick={saveCameras}>
                  <Save size={16} />
                  保存摄像头配置
                </Button>
              </Space>
            </Card>

            <div className="admin-camera-card-list">
              {cameraRows.map((camera) => (
                <CameraSlotCard camera={camera} key={camera.slot} updateCamera={updateCamera} />
              ))}
            </div>
          </section>
        </Tabs.Tab>

        <Tabs.Tab title="发现设备" key="devices">
          <section className="admin-mobile-panel">
            <Card className="admin-status-card">
              <div className="admin-status-row">
                <span>最近发现的 ONVIF 设备</span>
                <Button fill="outline" loading={isLoadingCameras} size="mini" onClick={() => loadCameras()}>
                  刷新
                </Button>
              </div>
              <p>电视 App 扫描后会同步到这里，点设备可自动填入第一个空槽位。</p>
            </Card>

            {discoveredDevices.length === 0 ? (
              <Card>
                <Empty description="暂无发现记录" />
              </Card>
            ) : (
              <div className="admin-device-card-list">
                {discoveredDevices.map((device) => (
                  <Card className="admin-device-card" key={`${device.ipAddress}-${device.xaddr}`}>
                    <strong>{device.name}</strong>
                    <span>{device.ipAddress}</span>
                    <code>{device.xaddr}</code>
                    <Button block color="primary" fill="outline" onClick={() => fillDiscoveredDevice(device)}>
                      填入空槽位
                    </Button>
                  </Card>
                ))}
              </div>
            )}
          </section>
        </Tabs.Tab>

        <Tabs.Tab title="用户" key="user">
          <section className="admin-mobile-panel">
            <Card className="admin-form-card" title="登录密码">
              <p className="admin-card-description">修改后下次登录使用新密码。当前已登录的页面会继续保持登录状态。</p>
              <MobileField label="当前登录密码">
                <Input aria-label="当前登录密码" clearable type="password" value={currentPassword} onChange={setCurrentPassword} />
              </MobileField>
              <MobileField label="新登录密码">
                <Input aria-label="新登录密码" clearable type="password" value={newPassword} onChange={setNewPassword} />
              </MobileField>
              <MobileField label="确认新登录密码">
                <Input aria-label="确认新登录密码" clearable type="password" value={confirmPassword} onChange={setConfirmPassword} />
              </MobileField>
              <Button block color="primary" loading={isChangingPassword} onClick={updateLoginPassword}>
                修改登录密码
              </Button>
            </Card>
          </section>
        </Tabs.Tab>
      </Tabs>
    </main>
  );
}

interface CameraSlotCardProps {
  camera: AdminCameraRow;
  updateCamera: (slot: 1 | 2 | 3 | 4, patch: Partial<AdminCameraRow>) => void;
}

function CameraSlotCard({ camera, updateCamera }: CameraSlotCardProps) {
  return (
    <Card
      className="admin-camera-slot-card"
      data-testid={`admin-camera-slot-${camera.slot}`}
      title={
        <div className="admin-camera-card-title">
          <span>{camera.slot}号摄像头</span>
          <Tag color={camera.hasPassword ? "success" : "danger"}>{camera.hasPassword ? "已保存密码" : "未保存密码"}</Tag>
        </div>
      }
    >
      <MobileField label={`${camera.slot}号摄像头名称`}>
        <Input aria-label={`${camera.slot}号摄像头名称`} clearable value={camera.name} onChange={(value) => updateCamera(camera.slot, { name: value })} />
      </MobileField>
      <MobileField label={`${camera.slot}号ONVIF地址`}>
        <Input aria-label={`${camera.slot}号ONVIF地址`} clearable value={camera.onvifUrl} onChange={(value) => updateCamera(camera.slot, { onvifUrl: value })} />
      </MobileField>
      <MobileField label={`${camera.slot}号RTSP地址`}>
        <Input aria-label={`${camera.slot}号RTSP地址`} clearable value={camera.rtspUrl} onChange={(value) => updateCamera(camera.slot, { rtspUrl: value })} />
      </MobileField>
      <MobileField label={`${camera.slot}号摄像头账号`}>
        <Input aria-label={`${camera.slot}号摄像头账号`} clearable value={camera.username} onChange={(value) => updateCamera(camera.slot, { username: value })} />
      </MobileField>
      <MobileField label={`${camera.slot}号摄像头密码`} hint="留空保持原密码">
        <Input
          aria-label={`${camera.slot}号摄像头密码`}
          clearable
          placeholder="留空保持原密码"
          type="password"
          value={camera.password}
          onChange={(value) => updateCamera(camera.slot, { password: value, clearPassword: false })}
        />
      </MobileField>
      <div className="admin-checkbox-row">
        <Checkbox
          aria-label={`${camera.slot}号清除密码`}
          checked={camera.clearPassword}
          onChange={(checked) => updateCamera(camera.slot, { clearPassword: checked, password: checked ? "" : camera.password })}
        >
          清除已保存密码
        </Checkbox>
      </div>
    </Card>
  );
}

interface PanelStatusProps {
  description: string;
  status: string;
  title: string;
}

function PanelStatus({ description, status, title }: PanelStatusProps) {
  return (
    <Card className="admin-status-card">
      <div className="admin-status-row">
        <span>{title}</span>
        <Tag color={status === "已保存" || status === "已加载" ? "success" : "primary"}>{status}</Tag>
      </div>
      <p>{description}</p>
    </Card>
  );
}

interface MobileFieldProps {
  children: ReactNode;
  hint?: string;
  label: string;
}

function MobileField({ children, hint, label }: MobileFieldProps) {
  return (
    <label className="admin-mobile-field">
      <span>{label}</span>
      {children}
      {hint ? <small>{hint}</small> : null}
    </label>
  );
}

interface IntegerInputProps {
  ariaLabel: string;
  onChange: (value: number) => void;
  value: number;
}

function IntegerInput({ ariaLabel, onChange, value }: IntegerInputProps) {
  return (
    <Input
      aria-label={ariaLabel}
      clearable
      inputMode="numeric"
      pattern="[0-9]*"
      value={String(value)}
      onChange={(nextValue) => {
        if (/^\d*$/.test(nextValue)) {
          onChange(toNumber(nextValue));
        }
      }}
    />
  );
}

interface RangeFieldProps {
  description: string;
  label: string;
  max: number;
  min: number;
  onChange: (value: number) => void;
  unit: string;
  value: number;
}

function RangeField({ description, label, max, min, onChange, unit, value }: RangeFieldProps) {
  return (
    <label className="admin-mobile-field admin-range-field">
      <span>{label}</span>
      <div className="admin-range-value">
        当前 {value} {unit}
      </div>
      <input
        aria-label={label}
        className="admin-range-input"
        max={max}
        min={min}
        step="1"
        type="range"
        value={value}
        onChange={(event) => onChange(toNumber(event.currentTarget.value))}
      />
      <small>{description}</small>
    </label>
  );
}

interface FilePickerProps {
  onChange: (file: File | undefined) => void;
}

function FilePicker({ onChange }: FilePickerProps) {
  return (
    <label className="admin-upload-field">
      <input accept="image/*" aria-label="从相册选择图片" type="file" onChange={(event) => onChange(event.currentTarget.files?.[0])} />
      <span>
        <ImagePlus size={16} />
        从相册选择图片
      </span>
    </label>
  );
}

interface AssetPreviewProps {
  alt: string;
  src?: string;
}

function AssetPreview({ alt, src }: AssetPreviewProps) {
  if (!src) {
    return <div className="admin-asset-preview empty">暂无图片</div>;
  }

  return (
    <div className="admin-asset-preview">
      <img alt={alt} src={src} />
    </div>
  );
}

function createDefaultCameraRows(snapshot: PublicDisplaySnapshot): AdminCameraRow[] {
  return ([1, 2, 3, 4] as const).map((slot) => {
    const camera = snapshot.cameras.find((item) => item.slot === slot);
    return {
      id: camera?.id ?? `cam-${slot}`,
      name: camera?.name ?? `摄像头${slot}`,
      slot,
      onvifUrl: "",
      ipAddress: "",
      rtspUrl: "",
      username: "",
      password: "",
      hasPassword: false,
      clearPassword: false
    };
  });
}

function mergeCameraRows(cameras: NonNullable<AdminCameraResponse["cameras"]>, snapshot: PublicDisplaySnapshot): AdminCameraRow[] {
  const defaults = createDefaultCameraRows(snapshot);
  return defaults.map((row) => {
    const camera = cameras.find((item) => item.slot === row.slot);
    if (!camera) {
      return row;
    }
    return {
      ...row,
      id: camera.id ?? row.id,
      name: camera.name ?? row.name,
      onvifUrl: camera.onvifUrl ?? "",
      ipAddress: camera.ipAddress ?? "",
      rtspUrl: camera.rtspUrl ?? "",
      username: camera.username ?? "",
      password: "",
      hasPassword: camera.hasPassword === true,
      clearPassword: false
    };
  });
}

function normalizeSnapshot(snapshot: PublicDisplaySnapshot): PublicDisplaySnapshot {
  return {
    ...snapshot,
    attendanceRule: normalizeAttendanceRule(snapshot.attendanceRule),
    licenseCards: ensureLicenseCards(snapshot.licenseCards),
    healthCertificates: buildHealthCertificateSummary(snapshot.healthCertificates.rows),
    sampleRetentionRows: snapshot.sampleRetentionRows.map((row, index) => ({ ...row, id: index + 1 })),
    reportUploads: snapshot.reportUploads ?? []
  };
}

function normalizeAttendanceRule(rule: AttendanceRule): AttendanceRule {
  return {
    ...rule,
    frequencyMinutes: clampNumber(rule.frequencyMinutes ?? 5, 1, 20),
    jitter: clampNumber(rule.jitter, 0, 50)
  };
}

function clampNumber(value: number, min: number, max: number): number {
  if (!Number.isFinite(value)) {
    return min;
  }

  return Math.min(max, Math.max(min, Math.round(value)));
}

function ensureLicenseCards(cards: LicenseCard[]): LicenseCard[] {
  const defaults: LicenseCard[] = [
    {
      id: "business-license",
      title: "营业执照",
      subtitle: "主体资质有效",
      imageUrl: "",
      updatedAt: ""
    },
    {
      id: "food-permit",
      title: "食品经营许可证",
      subtitle: "许可信息有效",
      imageUrl: "",
      updatedAt: ""
    }
  ];

  return defaults.map((fallback, index) => ({ ...fallback, ...(cards[index] ?? {}) }));
}

function healthStatusLabel(status: HealthStatus): string {
  return HEALTH_STATUS_LABELS.find((item) => item.value === status)?.label ?? "未知";
}

function healthStatusTagColor(status: HealthStatus): "danger" | "primary" | "success" | "warning" {
  if (status === "normal") {
    return "success";
  }
  if (status === "expiring") {
    return "warning";
  }
  if (status === "missing") {
    return "danger";
  }
  return "danger";
}

function toDateInputValue(value: string): string {
  return /^\d{4}-\d{2}-\d{2}$/.test(value) ? value : "";
}

function latestReportByCategory(reports: QualityReportUpload[], category: ReportCategory): QualityReportUpload | undefined {
  return reports
    .filter((report) => report.category === category)
    .sort((left, right) => new Date(right.uploadedAt).getTime() - new Date(left.uploadedAt).getTime())[0];
}

function extractSnapshot(response: PublicDisplaySnapshot | SnapshotSaveResponse, fallback: PublicDisplaySnapshot): PublicDisplaySnapshot {
  if ("snapshot" in response && response.snapshot) {
    return response.snapshot;
  }
  if ("title" in response) {
    return response;
  }
  return fallback;
}

function toNumber(value: string): number {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
}

function toLocalDateKey(value: string): string {
  return new Date(value).toLocaleDateString("zh-CN", { day: "2-digit", month: "2-digit", timeZone: "Asia/Shanghai", year: "numeric" });
}

async function readFileAsBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.addEventListener("load", () => {
      const result = String(reader.result ?? "");
      resolve(result.includes(",") ? result.split(",")[1] : result);
    });
    reader.addEventListener("error", () => reject(reader.error ?? new Error("读取文件失败")));
    reader.readAsDataURL(file);
  });
}

function getErrorMessage(error: unknown): string {
  return error instanceof Error ? error.message : "未知错误";
}
